import { Frequency, SubmissionStatus } from "generated-graphql/graphql";
import { partition } from "lodash-es";

import { Row } from "./types";

/**
 * Statuses where bulk add reviewers and bulk set signer actions are locked.
 * These submissions are too far in the workflow to allow reviewer/signer changes.
 */
export const BULK_ACTION_LOCKED_STATUSES: SubmissionStatus[] = [
  SubmissionStatus.EncampInitialReview,
  SubmissionStatus.InReview,
  SubmissionStatus.ProcessingSubmission,
  SubmissionStatus.FilingComplete,
];

/**
 * Checks if a submission status is locked for bulk reviewer/signer actions.
 */
export function isSubmissionLockedForBulkAction(
  status: SubmissionStatus | null | undefined
): boolean {
  return status ? BULK_ACTION_LOCKED_STATUSES.includes(status) : false;
}

export interface PartitionedSubmissions {
  realSubmissions: Row[];
  virtualEvents: Row[];
  uniqueSubmissionIds: string[];
  virtualEventIds: string[];
}

/**
 * Partitions submissions into materialized submissions and virtual events based on task frequency.
 *
 * Detection logic:
 * - Materialized instances: Task frequency === ONCE (created by createSubmission)
 *   These are real submissions that were materialized from a virtual event through
 *   the proper create flow. They have their own Task (ONCE) + Submission record.
 *
 * - Virtual events: Task frequency !== ONCE (YEARLY, MONTHLY, etc.)
 *   These are computed occurrences from recurring tasks. All virtual events from
 *   the same recurring task share a template Submission record (NOT_STARTED status).
 *   They need to be materialized before they can be worked on.
 *
 * NOTE: We cannot rely on submission.id existing to detect virtual events because
 * template submissions are created upfront by the create-submission-tasks lambda.
 */
export function partitionSubmissions(submissions: Row[]): PartitionedSubmissions {
  const [realSubmissions, virtualEvents] = partition(
    submissions,
    (s) => s.frequency === Frequency.Once
  );

  const uniqueSubmissionIds = [
    ...new Set(
      realSubmissions.map((s) => s.submission?.id).filter((id): id is string => Boolean(id))
    ),
  ];

  const virtualEventIds = virtualEvents.map((s) => s.id);

  return {
    realSubmissions,
    virtualEvents,
    uniqueSubmissionIds,
    virtualEventIds,
  };
}

/**
 * Formats a virtual event for display in a data grid
 */
export function formatVirtualEventForGrid(virtualEvent: Row) {
  return {
    id: virtualEvent.id,
    isVirtual: true,
    submission: {
      dueDate: virtualEvent.deadline,
      title: virtualEvent.title,
      submissionType: virtualEvent.submission?.submissionType,
      status: null,
    },
    facilities: virtualEvent.facilities,
  };
}
